import React, { useCallback, useState } from 'react';
import { ExpireHoldButton } from 'src/components/Buttons/ExpireHoldButton';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { ConfirmDialog } from 'src/core/interim/dialogs/ConfirmDialog';
import { ButtonProps } from 'src/core/ui';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { ContentId } from 'src/utils/constants/contentId';
import { isSuccess } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  ActivityLogClient,
  ApiException,
  SaleClient,
  SaleDetails,
} from 'src/WebApiController';

export const LaunchExpireHold = ({
  saleId,
  marketplaceSaleId,
  variant,
  iconOnlyMode,
  disabled,
}: {
  saleId: number;
  marketplaceSaleId?: string | null;
  variant?: ButtonProps['variant'];
  iconOnlyMode?: boolean;
  disabled?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { showErrorDialog, genericError, trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<SaleDetails>();

  const { launchDialog, closeDialog, dialogProps } = useBasicDialog();

  const expireHold = useCallback(() => {
    setIsLoading(true);
    tryInvokeApi(
      async () => {
        const client = new SaleClient(activeAccountWebClientConfig);

        const result = await client.expireHold(saleId);
        if (!isSuccess(result)) {
          showErrorDialog(
            'SaleClient.ExpireHold',
            {
              message: result.message ?? genericError,
              status: result.status!,
            } as ApiException,
            {
              trackErrorData: { saleId },
            }
          );
        } else {
          await setActivePosEntity(saleId, marketplaceSaleId, true);
        }
      },
      (error) => {
        showErrorDialog('SaleClient.ExpireHold', error, {
          trackErrorData: { saleId },
        });
      },
      () => {
        setIsLoading(false);
        closeDialog();
      }
    );
  }, [
    activeAccountWebClientConfig,
    closeDialog,
    genericError,
    marketplaceSaleId,
    saleId,
    setActivePosEntity,
    showErrorDialog,
  ]);

  const onLaunchDialog = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      tryInvokeApi(
        async () => {
          await new ActivityLogClient(
            activeAccountWebClientConfig
          ).trackEntityView(saleId, ActionOutboxEntityType.Sale);
        },
        (error) => {
          trackError('ActivityLogClient.trackEntityView', error, { saleId });
        }
      );
      launchDialog();
    },
    [launchDialog, activeAccountWebClientConfig, saleId, trackError]
  );

  return (
    <div>
      <ExpireHoldButton
        onClick={onLaunchDialog}
        variant={variant}
        disabled={disabled || isLoading}
        iconOnlyMode={iconOnlyMode}
      />
      <ConfirmDialog
        {...dialogProps}
        disabled={isLoading}
        isLoading={isLoading}
        headerText={<Content id={ContentId.ExpireHold} />}
        bodyText={<Content id={ContentId.ExpireHoldWarning} />}
        onOkay={expireHold}
      />
    </div>
  );
};
